import { ReactNode } from "react";
import { Box, IconButton, MenuItem, Select, Typography } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../i18n";

interface Props {
  count: number;
  /** 0-based page index */
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  /** Overrides the "Rows per page:" label (e.g. "Sectors per page:"). */
  label?: string;
  /** Rendered flush left, opposite the pager — e.g. a row-count summary. */
  startContent?: ReactNode;
}

/** Table footer pager — Figma Table/Bottom_Bar (node 6391:12557):
 *  right-aligned, top border, "Rows per page: [n] · {from} to {to} · first
 *  prev next last". Grey 16px text, 24px arrow buttons. */
const FePagination = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
  label,
  startContent,
}: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="12px"
      width="100%"
      px="24px"
      py="12px"
      sx={{ borderTop: "1px solid var(--gray5)" }}
      color="var(--gray1)"
    >
      {startContent ?? <Box />}
      <Box display="flex" alignItems="center" gap="12px">
      <Box display="flex" alignItems="center" gap="4px">
        <Typography
          fontSize="16px"
          fontWeight={500}
          lineHeight="24px"
          color="var(--gray1)"
        >
          {label ?? t("Rows-Per-Page")}
        </Typography>
        <Select
          variant="standard"
          disableUnderline
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            color: "var(--gray1)",
            "& .MuiSelect-select": { py: 0, pr: "24px !important" },
          }}
        >
          {rowsPerPageOptions.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Typography
        fontSize="16px"
        fontWeight={500}
        lineHeight="24px"
        color="var(--gray1)"
      >
        {t("Pagination-Range", { from, to })}
      </Typography>
      <Box display="flex" alignItems="center" gap="8px">
        <IconButton
          size="small"
          disabled={page <= 0}
          onClick={() => onPageChange(0)}
          sx={{ color: "var(--gray1)", p: 0 }}
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          size="small"
          disabled={page <= 0}
          onClick={() => onPageChange(page - 1)}
          sx={{ color: "var(--gray1)", p: 0 }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <IconButton
          size="small"
          disabled={page >= lastPage}
          onClick={() => onPageChange(page + 1)}
          sx={{ color: "var(--gray1)", p: 0 }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
        <IconButton
          size="small"
          disabled={page >= lastPage}
          onClick={() => onPageChange(lastPage)}
          sx={{ color: "var(--gray1)", p: 0 }}
        >
          <LastPageIcon />
        </IconButton>
      </Box>
      </Box>
    </Box>
  );
};

export default FePagination;
